import { Classes, IconName, MaybeElement } from "@blueprintjs/core";
import { FGCustomPanel, FormGenerator, IFGContext, IFormGeneratorProps, Loading } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { useAuth } from "../contexts";
import { useTl } from "../hooks";
import { ETLCodes } from "../locales";
import {
  CancelButton,
  DeleteButton,
  DuplicateButton,
  EditButton,
  IDeleteButtonProps,
  SaveButton
} from "./applicationButtons";
import { ButtonsBloc, InlineButtonContainer } from "./applicationButtons/ButtonContainerStyles";
import { FGRouteDirtyChecker } from "./formGenerator/FGRouteDirtyChecker";
import * as Yup from "yup";
import { add, format } from "date-fns";

interface IFormDeleteButtonProps extends Omit<IDeleteButtonProps, "onClick" | "minimal"> {
  icon?: IconName | MaybeElement;
}

interface ISmallFormGeneratorProps<T> extends Omit<IFormGeneratorProps<T>, "children"> {
  loading?: boolean;
  onEdit?: () => void;
  onCancel?: () => void;
  onDelete?: () => void;
  deleting?: boolean;
  deleteBtnDisabled?: boolean;
  saving?: boolean;
  showDeleteButton?: boolean;
  editMode?: boolean;
  hideButtons?: boolean;
  cancelEdit?: boolean;
  enableDirtyCheck?: boolean;
  editable?: boolean;
  canCheckRights?: boolean;
  rightSaveButton?: boolean;
  onHardDelete?: () => void;
  hardDeleting?: boolean;
  forceEnableSave?: boolean;
  showDuplicateButton?: boolean;
  onDuplicate?: () => void;
  additionalRightButtons?: (ctx: IFGContext<any>) => JSX.Element;
  deleteButtonProps?: IFormDeleteButtonProps;
  customDeleteButtonProps?: IFormDeleteButtonProps;
  specificRights?: { module: string; tab?: string; rights?: "R" | "RW" | "RWD" };
  disableSaveButton?: boolean;
  showRightButtons?: boolean;
}

const StyledForm = styled.div`
  & .${Classes.FORM_GROUP} {
    margin: 0 0 2px !important;
  }

  & .${Classes.CONTROL} {
    margin-bottom: 0 !important;
  }
`;

export function SmallFormGenerator<T>(props: React.PropsWithChildren<ISmallFormGeneratorProps<T>>) {
  const {
    editable = true,
    children,
    loading = false,
    showDeleteButton = false,
    deleting,
    onDelete,
    deleteBtnDisabled = false,
    onEdit = () => {},
    onCancel,
    saving,
    editMode,
    minLabelWidth = 180,
    labelAlignment = "right",
    fill = true,
    showColons = true,
    inline = true,
    boldLabel = true,
    hideButtons = false,
    enableDirtyCheck = true,
    cancelEdit = false,
    validationErrors = {},
    canCheckRights = true,
    rightSaveButton = false,
    onHardDelete,
    hardDeleting,
    forceEnableSave = false,
    showDuplicateButton = false,
    onDuplicate,
    additionalRightButtons,
    deleteButtonProps,
    customDeleteButtonProps,
    specificRights,
    disableSaveButton = false,
    validationSchema,
    showRightButtons = true,
    ...otherProps
  } = props;
  const [innerEditMode, setInnerEditMode] = React.useState(editMode);

  React.useEffect(() => {
    setInnerEditMode(editMode);
  }, [editMode]);

  const { getCurrentPermission, hasPermission } = useAuth();

  const readOnly = React.useMemo(() => canCheckRights && getCurrentPermission()?.permission === "R", [
    canCheckRights,
    getCurrentPermission
  ]);

  const canDelete = React.useMemo(
    () =>
      !!specificRights
        ? hasPermission(specificRights.module, specificRights.rights ?? "RWD", specificRights.tab)
        : canCheckRights && getCurrentPermission()?.permission === "RWD",
    [canCheckRights, getCurrentPermission, hasPermission, specificRights]
  );

  const { t, tUnsafe } = useTl();

  const [isDeleting, setIsDeleting] = React.useState(false);
  const [isSoftDeleting, setIsHardDeleting] = React.useState(false);

  const editCallback = React.useCallback(() => {
    setInnerEditMode(true);
    if (onEdit) {
      onEdit();
    }
  }, [onEdit]);

  const deleteCallback = React.useCallback(() => {
    setIsDeleting(true);
    if (onDelete) {
      onDelete();
    }
  }, [onDelete]);

  const hardDeleteCallback = React.useCallback(() => {
    setIsHardDeleting(true);
    if (onHardDelete) {
      onHardDelete();
    }
  }, [onHardDelete]);

  const finalValidationSchema = React.useMemo<any>(() => {
    if (!validationSchema) return;

    const maxDate = add(new Date(), { years: 100 });
    const fields = { ...validationSchema["fields"] };
    Object.keys(fields).forEach(key => {
      if (
        fields[key] instanceof Yup.DateSchema &&
        (fields[key] as Yup.DateSchema).tests.every(f => f.OPTIONS.name !== "min")
      ) {
        fields[key] = fields[key].min(new Date(1900, 1, 1), t(ETLCodes.DateMinimale));
      }
      if (
        fields[key] instanceof Yup.DateSchema &&
        (fields[key] as Yup.DateSchema).tests.every(f => f.OPTIONS.name !== "max")
      ) {
        fields[key] = fields[key].max(maxDate, t(ETLCodes.DateMaximale, { maxDate: format(maxDate, "dd/MM/yyyy") }));
      }
    });

    validationSchema["fields"] = fields;
    return validationSchema;
  }, [t, validationSchema]);

  return (
    <StyledForm>
      <Loading isLoading={loading}>
        {(props?.initialValues || props.formik?.initialValues) && (
          <FormGenerator
            editMode={innerEditMode && !readOnly}
            minLabelWidth={minLabelWidth}
            labelAlignment={labelAlignment}
            fill={fill}
            inline={inline}
            boldLabel={boldLabel}
            showColons={showColons}
            validationErrors={validationErrors}
            trim={true}
            touchOnSubmit
            translateFn={tUnsafe}
            validationSchema={finalValidationSchema}
            {...otherProps}
          >
            {enableDirtyCheck && (
              <FGRouteDirtyChecker
                isDelete={isDeleting || isSoftDeleting}
                isViewMode={!innerEditMode}
                enableDirtyCheck={enableDirtyCheck}
              />
            )}
            {children}
            {!hideButtons && (
              <FGCustomPanel>
                {ctx => (
                  <InlineButtonContainer>
                    <ButtonsBloc>
                      {customDeleteButtonProps && (
                        <DeleteButton {...customDeleteButtonProps} minimal={false} specificRights={specificRights} />
                      )}
                      {showDeleteButton && onDelete && innerEditMode && canDelete ? (
                        <DeleteButton
                          {...deleteButtonProps}
                          minimal={false}
                          loading={deleting}
                          onDelete={deleteCallback}
                          disabled={deleteBtnDisabled}
                          specificRights={specificRights}
                        />
                      ) : (
                        <></>
                      )}
                      {onHardDelete && innerEditMode && (
                        <DeleteButton
                          minimal={false}
                          loading={hardDeleting}
                          onDelete={hardDeleteCallback}
                          text={t(ETLCodes.HardDelete)}
                          specificRights={specificRights}
                        />
                      )}
                      {rightSaveButton &&
                        (innerEditMode ? (
                          <SaveButton
                            minimal={false}
                            loading={saving}
                            disabled={!ctx.formik.dirty}
                            specificRights={specificRights}
                          />
                        ) : (
                          editable && !readOnly && <EditButton minimal={false} onClick={editCallback}></EditButton>
                        ))}
                    </ButtonsBloc>
                    {showRightButtons && (
                      <ButtonsBloc>
                        {!onCancel && cancelEdit && innerEditMode && (
                          <CancelButton minimal={false} onClick={() => setInnerEditMode(false)} />
                        )}
                        {onCancel && <CancelButton minimal={false} onClick={onCancel} />}
                        {showDuplicateButton ? (
                          <DuplicateButton minimal={false} onClick={onDuplicate}></DuplicateButton>
                        ) : (
                          <></>
                        )}
                        {innerEditMode ? (
                          <SaveButton
                            minimal={false}
                            loading={saving}
                            disabled={(!ctx.formik.dirty && !forceEnableSave) || disableSaveButton}
                            specificRights={specificRights}
                          />
                        ) : (
                          editable && !readOnly && <EditButton minimal={false} onClick={editCallback}></EditButton>
                        )}
                        {!!additionalRightButtons ? additionalRightButtons(ctx) : null}
                      </ButtonsBloc>
                    )}
                  </InlineButtonContainer>
                )}
              </FGCustomPanel>
            )}
          </FormGenerator>
        )}
      </Loading>
    </StyledForm>
  );
}
